import {
  Component,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthService } from '../../service/auth/auth.service';
import { CustomerJourneyService } from '../../service/customer-journey/customer-journey.service';
import { RegisterAuthState } from '../../ngxs/auth/auth.actions';
import { QueryParamsService } from '../../service/query-params/query-params.service';
import { Subscription } from 'rxjs';
import { LoggingService } from 'src/app/service/logging-service/logging.service';
import { routeNames } from '@assets/val/route-constants';
import { CreditService } from '@app/service/credit/credit.service';
import QRCodeStyling from 'qr-code-styling';
import { AppDeepLinkService } from '@app/service/app-deeplink/app-deep-link.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PaymentRequestDto } from '@app/model/payment-request/payment-request.dto';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css', '../page-shared.css'],
})
export class AuthComponent implements AfterViewInit, OnDestroy {
  paymentRequest: PaymentRequestDto;
  loading: boolean = true;

  tryAgainButtonText = 'Försök igen';
  useThisDeviceButtonText = 'Öppna från denna enhet istället';
  useDifferentDeviceButtonText = 'Öppna från en annan enhet';

  manualDeeplinkEnabled = false;
  bankIdStatusMessage: string;
  authErrorMessageList: string[];

  public authInProgress = false;
  public showQrCode = false;

  // Temporary solution for users on the Tibber App
  public isTibberApp: boolean = false;
  public isMessageClicked: boolean = false;

  public loadingPreFillInfoFromServer = false;

  public isMobile = false;
  public navigating = false;

  @ViewChild('canvas') canvas: ElementRef;
  private qrCode = <QRCodeStyling>{};
  private qrCodeCurrentRetries = 0;
  private qrCodeMaxRetries = 3;

  private authSubscription: Subscription;
  private bankIdActivityTimer: any = null;

  private readonly scanQrCodeStatusMessage =
    'Starta BankID-appen i din mobil och tryck på ikonen för QR-kod. Rikta kameran mot QR-koden på den här sidan.';
  private readonly beforeBankIdAutoStartStatusMessage = 'Vänta...';
  private readonly bankIdStatusOutstandingTransaction =
    'OUTSTANDING_TRANSACTION';
  // TODO(seb): remove this once BE uses correct status messages for the qr code flow
  private readonly bankIdStatusStartFailed = 'START_FAILED';

  constructor(
    private authService: AuthService,
    private customerJourneyService: CustomerJourneyService,
    private router: Router,
    private store: Store,
    private creditService: CreditService,
    private deviceDetectorService: DeviceDetectorService,
    private queryParamsService: QueryParamsService,
    private loggingService: LoggingService,
    private deepLinkService: AppDeepLinkService
  ) {}

  async ngAfterViewInit(): Promise<void> {
    const paymentRequestId = this.queryParamsService.getQueryParams().id;

    if (paymentRequestId) {
      const res = await this.creditService
        .getPaymentRequestPublicInfo(paymentRequestId)
        .toPromise();

      // Only used to figure out if we should show the 'go back' button.
      this.paymentRequest = res;
      this.loading = false;
    }

    this.isMobile = this.deviceDetectorService.isMobile();
    this.showQrCode = !this.isMobile;

    if (this.showQrCode) {
      this.bankIdStatusMessage = this.scanQrCodeStatusMessage;
    } else {
      this.bankIdStatusMessage = this.beforeBankIdAutoStartStatusMessage;
    }

    const authId = this.deepLinkService.getCurrentAuthRefId();
    if (authId) {
      // this should only happen if a browser on iOS opens a new tab instead of reusing the old one
      // won't happen for Chrome and Firefox
      // shouldn't happen for Safari, although it's not documented and may change
      this.handlePotentialRedirectFromBankIdApp(authId);
    } else {
      this.authenticate();
    }
  }

  ngOnDestroy(): void {
    this.authService.stopPolling();

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }
  }

  private handlePotentialRedirectFromBankIdApp(authId: string): void {
    this.loggingService.debug('Coming back from bankid app');
    this.setupAuthInProgressSubscription();
    this.authService.continueAuth(authId);
  }

  private authenticate(): void {
    const queryParams = this.queryParamsService.getQueryParams();
    this.authErrorMessageList = null;

    this.loggingService.debug('should start sub');

    this.isTibberApp = /Tibber/i.test(this.deviceDetectorService.userAgent);

    if (this.isTibberApp) {
      this.loggingService.warn(
        'Tibber app detected! Displaying message to user to open the bankId app manually instead.'
      );
    }

    let useAutoStart = !this.showQrCode;

    // If autoStart is not explicitly set to false, we will auto start
    // the payment. However, if a specific payment method is set on
    // the payment request, we will not auto start, as that flow
    // might require user interaction before starting the payment.
    if (
      queryParams.autoStart == 'false' ||
      this.isTibberApp ||
      this.paymentRequest?.specificPaymentMethod
    ) {
      useAutoStart = false;
    }
    this.authService.authenticate(useAutoStart);

    this.setupAuthInProgressSubscription();
  }

  private indicateAuthInProgress(inProgress: boolean): void {
    if (inProgress) {
      this.authErrorMessageList = null;
      this.authInProgress = true;
    } else {
      this.authInProgress = false;
      this.manualDeeplinkEnabled = false;
      this.qrCodeCurrentRetries = 0;
    }
  }

  private setupAuthInProgressSubscription(): void {
    this.indicateAuthInProgress(true);

    // make sure that the old state is cleaned up before subscribing
    this.store.dispatch(RegisterAuthState.empty());

    if (!this.authSubscription) {
      this.authSubscription = this.store.subscribe((updatedState) => {
        const auth: RegisterAuthState = updatedState.auth;

        if (auth.inProgress === false && auth.authenticated === false) {
          this.handleUnsuccessfulAuth(auth);
        } else if (auth.authenticated) {
          this.handleSuccessfulAuth();
        } else if (auth.inProgress) {
          this.handleInProgress(auth);
        }
      });
    }
  }

  private handleUnsuccessfulAuth(auth: RegisterAuthState): void {
    this.authService.stopPolling();

    if (
      this.showQrCode &&
      this.qrCodeCurrentRetries < this.qrCodeMaxRetries &&
      (auth.status === this.bankIdStatusOutstandingTransaction ||
        auth.status === this.bankIdStatusStartFailed)
    ) {
      // BankId fails the auth after 30 seconds, but we want to show the QR code longer than that
      this.qrCodeCurrentRetries++;
      this.authService.authenticate(!this.showQrCode);
    } else {
      if (!!this.authSubscription) {
        this.authSubscription.unsubscribe();
        this.authSubscription = null;
      }

      this.indicateAuthInProgress(false);
      this.authErrorMessageList = [
        'Legitimering misslyckades.',
        'Vänligen försök igen.',
      ];

      if (this.bankIdActivityTimer !== null) {
        clearTimeout(this.bankIdActivityTimer);
        this.bankIdActivityTimer = null;
      }
    }
  }

  private handleSuccessfulAuth(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }
    this.authService.stopPolling();

    this.navigating = true;
    this.customerJourneyService.getNextRoute().subscribe((route) => {
      this.router
        .navigate([route], {
          queryParamsHandling: 'preserve',
        })
        .then(() => {
          this.navigating = false;
        });
    });
  }

  private handleInProgress(auth: RegisterAuthState): void {
    if (
      this.showQrCode &&
      (auth.status === '' ||
        auth.status === this.bankIdStatusOutstandingTransaction)
    ) {
      this.bankIdStatusMessage = this.scanQrCodeStatusMessage;
    } else {
      this.bankIdStatusMessage = auth.message;
    }

    if (
      auth.status === this.bankIdStatusOutstandingTransaction &&
      !this.showQrCode
    ) {
      if (!this.bankIdActivityTimer) {
        this.bankIdActivityTimer = setTimeout(() => {
          this.bankIdActivityTimer = null;
          // after a delay; show the button so that the user can start their bankid-app
          // with an already ongoing authentication
          clearTimeout(this.bankIdActivityTimer);
          this.bankIdActivityTimer = null;
          this.manualDeeplinkEnabled = true;
        }, 2000);
      }
    }

    this.updateQrCode(auth.qrCodeData);
  }

  private updateQrCode(data: string): void {
    if (this.showQrCode) {
      if (!this.qrCode._options) {
        this.qrCode = new QRCodeStyling({
          width: 180,
          height: 180,
          type: 'svg',
          margin: 0,
          data: data,
          cornersSquareOptions: {
            type: 'extra-rounded',
          },
          cornersDotOptions: {
            type: 'dot',
          },
          qrOptions: {
            errorCorrectionLevel: 'L',
          },
          backgroundOptions: {
            color: 'rgba(0,0,0,0)',
          },
        });

        this.qrCode.append(this.canvas.nativeElement);
      } else {
        this.qrCode.update({ data: data });
      }
    }
  }

  showQrCodeClick(): void {
    this.resetQrCodeComponent();
    this.showQrCode = true;

    if (!this.authInProgress) {
      this.authenticate();
    }
  }

  autoStartAppClick(): void {
    this.showQrCode = false;

    if (this.authInProgress) {
      this.authService.openBankIdApp();
    } else {
      this.authenticate();
    }
  }

  private resetQrCodeComponent(): void {
    if (this.qrCode._options) {
      this.qrCode.update({ data: '' });
    }
    this.qrCode = <QRCodeStyling>{};
  }

  reroute(): void {
    this.router.navigate([routeNames.HOME], {
      queryParamsHandling: 'merge',
    });
  }

  // Temporary solution for users on the Tibber App
  getTibberAppMessageList(): string[] {
    if (!this.isMessageClicked) {
      return [
        'Inuti Tibber appen?',
        '',
        'Klicka på detta meddelande för ',
        'att kopiera länken',
      ];
    }
    return ['Länken har kopierats!', '', 'Länk:', window.location.href];
  }

  // Temporary solution for users on the Tibber App
  copyCheckoutLinkToClipboard(): void {
    if (!this.isMessageClicked) {
      this.isMessageClicked = true;
      navigator.clipboard.writeText(window.location.href);
    }
  }
}
